<div class="aw" data-testid="account-workspace">
    <div class="aw-loading" data-testid="account-workspace-loading" *ngIf="loading; else notLoading">
        <div class="aw-skeleton" *ngFor="let row of skeletonRows" aria-hidden="true"></div>
    </div>

    <ng-template #notLoading>
        <div
            class="aw-error"
            data-testid="account-workspace-error"
            [attr.data-reason]="errorReason"
            *ngIf="error; else shell"
        >
            {{ (errorReason === 'not-found' ? 'abcp_workspace_error_not_found' : 'abcp_workspace_error') | i18next }}
        </div>

        <ng-template #shell>
            <div class="aw-head">
                <div class="aw-head__account">
                    <span class="aw-number" data-testid="account-number">{{ account?.number }}</span>
                    <span
                        class="aw-name"
                        data-testid="account-name"
                        [hamTooltip]="account?.name"
                        tooltipClass="abcp"
                        [tooltipOnlyWhenTruncated]="true"
                        >{{ account?.name }}</span
                    >
                </div>

                <!-- The document value next to the account name: one row, it wraps under the name when narrow. -->
                <ng-container *ngIf="reconciliation as rec">
                    <span class="aw-head__divider" aria-hidden="true"></span>
                    <div class="aw-balances">
                        <span class="aw-balance">
                            <span class="aw-balance__label">ERP</span>
                            <span class="aw-balance__amount" data-testid="erp-balance">
                                <ng-container
                                    *ngTemplateOutlet="money; context: { $implicit: rec.erpBalance }"
                                ></ng-container>
                            </span>
                        </span>

                        <span class="ham-icon aw-balances__arrow" aria-hidden="true">
                            <i class="arrow-right-arrow-left-icon"></i>
                        </span>

                        <!-- REV-22302 — one grey card for the document side: what the AI is doing, or the value. -->
                        <div
                            class="aw-ai-card"
                            data-testid="ai-reading-card"
                            [attr.data-state]="rec.aiReading?.state || null"
                        >
                            <span class="aw-balance__label">{{ 'abcp_workspace_document_balance' | i18next }}</span>

                            <span
                                class="aw-ai-card__skeleton"
                                data-testid="ai-reading-loading"
                                aria-hidden="true"
                                *ngIf="aiCardLoading"
                            ></span>
                            <ng-container *ngIf="!aiCardLoading" [ngSwitch]="rec.aiReading?.state">
                                <span class="ai-working" data-testid="ai-reading-working" role="status" *ngSwitchCase="'READING'">
                                    <span class="ai-working__dot" aria-hidden="true"></span>
                                    <span class="ai-working__text">
                                        <span class="ai-working__title">{{ 'abcp_documents_ai_working' | i18next }}</span>
                                        <span class="ai-working__step">{{ aiStepKey(rec.aiReading) | i18next }}</span>
                                    </span>
                                </span>

                                <span class="ai-working" data-testid="ai-reading-reconciling" role="status" *ngSwitchCase="'RECONCILING'">
                                    <span class="ai-working__dot" aria-hidden="true"></span>
                                    <span class="ai-working__text">
                                        <span class="ai-working__title">{{ 'abcp_workspace_ai_reconciling' | i18next }}</span>
                                        <span class="ai-working__step">{{ 'abcp_workspace_ai_reconciling_step' | i18next }}</span>
                                    </span>
                                </span>

                                <span class="aw-ai-card__main" *ngSwitchCase="'REFUSED'">
                                    <span class="aw-ai-card__status">{{ 'abcp_workspace_ai_refused' | i18next }}</span>
                                    <span class="aw-ai-card__detail" data-testid="ai-reading-reason" *ngIf="aiReasonKey(rec.aiReading?.reason) as reasonKey">{{
                                        reasonKey | i18next
                                    }}</span>
                                </span>

                                <span class="aw-ai-card__main" *ngSwitchCase="'OPEN'">
                                    <span class="aw-ai-card__status">{{ 'abcp_workspace_ai_open' | i18next }}</span>
                                    <span class="aw-ai-card__detail" data-testid="ai-reading-reason" *ngIf="aiReasonKey(rec.aiReading?.reason) as reasonKey">{{
                                        reasonKey | i18next
                                    }}</span>
                                </span>

                                <span class="aw-ai-card__main" *ngSwitchDefault>
                                    <span
                                        class="aw-balance__amount"
                                        data-testid="document-balance"
                                        [hamTooltip]="readingHint(rec.aiReading)"
                                        tooltipClass="abcp"
                                    >
                                        <ng-container
                                            *ngTemplateOutlet="money; context: { $implicit: rec.aiReading?.amount || rec.documentBalance }"
                                        ></ng-container>
                                    </span>
                                    <!-- REV-22005 (N-2.2) — the value was typed by a trustee, not read by the machine. -->
                                    <span
                                        class="aw-balance__origin"
                                        data-testid="document-balance-origin"
                                        *ngIf="
                                            rec.aiReading?.state === 'MANUAL' ||
                                            (rec.documentBalanceOrigin === 'HUMAN' && rec.documentBalance)
                                        "
                                    >
                                        {{ 'abcp_workspace_document_balance_manual' | i18next }}
                                    </span>
                                </span>
                            </ng-container>

                            <ham-button
                                class="aw-pencil"
                                data-testid="document-balance-edit"
                                *ngIf="rec.documentBalanceEditable && !aiCardLoading && rec.aiReading?.state !== 'READING'"
                                variant="transparent"
                                size="xs"
                                [hideText]="true"
                                icon="/assets/hamIcons/pen-outline.svg"
                                (buttonClick)="manualEntryOpen.emit()"
                            ></ham-button>

                            <!-- While the AI reads or the run checks, the card says what happens; an older status would mislead. -->
                            <span
                                class="aw-verdict"
                                *ngIf="!aiCardLoading && rec.aiReading?.state !== 'READING' && rec.aiReading?.state !== 'RECONCILING'"
                            >
                                <span class="aw-verdict__chips">
                                    <ham-badge-button
                                        class="aw-status"
                                        data-testid="reconciliation-status"
                                        [attr.data-status]="rec.status"
                                        [variant]="statusVariant(rec.status)"
                                    >
                                        {{ statusLabelKey(rec.status) | i18next }}
                                    </ham-badge-button>

                                    <ham-badge-button
                                        class="aw-difference"
                                        data-testid="difference"
                                        *ngIf="rec.status === 'difference' && rec.difference"
                                        variant="red"
                                    >
                                        <ng-container *ngTemplateOutlet="money; context: { $implicit: rec.difference }"></ng-container>
                                    </ham-badge-button>

                                    <!-- A hand-entered value that is not a difference — open again after an ERP sync —
                                         would otherwise hide what separates it from the ERP figure. Gray, not red: it
                                         is not a finding. Only when it is not zero. -->
                                    <ham-badge-button
                                        class="aw-difference aw-difference--manual"
                                        data-testid="difference-manual"
                                        *ngIf="
                                            rec.status !== 'difference' &&
                                            rec.documentBalanceOrigin === 'HUMAN' &&
                                            rec.difference &&
                                            rec.difference.amount !== 0
                                        "
                                        variant="gray"
                                    >
                                        <ng-container *ngTemplateOutlet="money; context: { $implicit: rec.difference }"></ng-container>
                                    </ham-badge-button>
                                </span>

                                <!-- Why the chip says what it says. Differenz has no line: the numbers are on the card. -->
                                <span
                                    class="aw-reason"
                                    data-testid="account-reason"
                                    [attr.data-code]="rec.reason?.code"
                                    *ngIf="reasonLine as line"
                                    >{{ line.key | i18next: line.params }}</span
                                >
                            </span>
                        </div>
                    </div>
                </ng-container>

                <div class="aw-head__actions" *ngIf="showReview || showBack">
                    <ng-container *ngIf="showReview">
                        <ham-button
                            class="aw-review aw-review--marked"
                            data-testid="review-toggle"
                            data-marked="true"
                            *ngIf="reviewMarked; else reviewUnmarkedButton"
                            variant="white"
                            outline
                            icon="/assets/hamIcons/flag.svg"
                            rightIcon="/assets/hamIcons/x.svg"
                            (buttonClick)="reviewUnmark.emit()"
                        >
                            {{ 'abcp_workspace_review_marked' | i18next }}
                        </ham-button>

                        <ng-template #reviewUnmarkedButton>
                            <ham-button
                                class="aw-review"
                                data-testid="review-toggle"
                                data-marked="false"
                                variant="white"
                                outline
                                icon="/assets/hamIcons/flag.svg"
                                (buttonClick)="reviewMarkOpen.emit()"
                            >
                                {{ 'abcp_workspace_review_mark' | i18next }}
                            </ham-button>
                        </ng-template>

                        <span
                            class="aw-review-error"
                            data-testid="review-error"
                            role="alert"
                            *ngIf="reviewError && !reviewDialog"
                        >
                            {{ reviewError }}
                        </span>
                    </ng-container>

                    <ham-button
                        class="aw-back"
                        data-testid="back"
                        *ngIf="showBack"
                        variant="white"
                        outline
                        icon="/assets/hamIcons/x.svg"
                        (buttonClick)="backSelect.emit()"
                    >
                        {{ 'abcp_workspace_back' | i18next }}
                    </ham-button>
                </div>
            </div>

            <div class="aw-reconciliation" *ngIf="manualEntry">
                <div class="aw-entry" data-testid="manual-entry" *ngIf="manualEntry">
                    <label class="aw-entry__label">{{ amountLabel }}</label>
                    <!-- type="number": digits only, and it is what makes decimalPlaces work — the
                         directive then shows the value formatted and swaps to the field on focus. -->
                    <input
                        hamInput
                        class="aw-entry__field aw-entry__field--amount"
                        data-testid="manual-entry-amount"
                        type="number"
                        step="0.01"
                        [decimalPlaces]="2"
                        [hideRemoveXButton]="true"
                        [ngModel]="amountDraft"
                        [ngModelOptions]="{ standalone: true }"
                        (ngModelChange)="amountDraft = $event"
                    />

                    <label class="aw-entry__label">{{ 'abcp_workspace_reference' | i18next }}</label>
                    <input
                        hamInput
                        class="aw-entry__field"
                        data-testid="manual-entry-reference"
                        [hideRemoveXButton]="true"
                        [ngModel]="referenceDraft"
                        [ngModelOptions]="{ standalone: true }"
                        (ngModelChange)="referenceDraft = $event"
                    />

                    <ham-button
                        data-testid="manual-entry-confirm"
                        variant="teal"
                        icon="/assets/hamIcons/check.svg"
                        [disabled]="confirmDisabled || saving"
                        [attr.aria-disabled]="confirmDisabled || saving"
                        (buttonClick)="onConfirmManualEntry()"
                    >
                        {{ 'abcp_workspace_confirm' | i18next }}
                    </ham-button>

                    <ham-button
                        data-testid="manual-entry-cancel"
                        variant="white"
                        outline
                        (buttonClick)="manualEntryCancel.emit()"
                    >
                        {{ 'abcp_workspace_cancel' | i18next }}
                    </ham-button>

                    <!-- REV-22005 — the write failed; the row stays open with what was typed. -->
                    <span class="aw-entry__error" data-testid="manual-entry-error" role="alert" *ngIf="entryError">
                        {{ entryError }}
                    </span>
                </div>
            </div>

            <!-- The panes, and on their right a narrow column with the + that adds one. -->
            <div class="aw-body">
                <div
                    #paneStrip
                    class="aw-panes"
                    cdkDropList
                    cdkDropListOrientation="horizontal"
                    (cdkDropListDropped)="onPaneDrop($event)"
                >
                    <ng-container *ngFor="let pane of panes; let i = index; trackBy: trackByPaneId">
                        <div
                            #paneElement
                            class="aw-pane"
                            data-testid="pane"
                            [attr.data-kind]="pane.kind"
                            [style.flex]="paneFlex(pane)"
                            cdkDrag
                            cdkDragLockAxis="x"
                            [cdkDragDisabled]="panes.length <= 1"
                        >
                            <ng-template cdkDragPreview>
                                <div class="aw-drag-preview">{{ paneKindLabel(pane.kind) }}</div>
                            </ng-template>

                            <!-- The divider belongs to the column, not to the strip: as a sibling of the
                                 columns it would stay behind when CDK sorts. It resizes the column to
                                 its left and its right-hand neighbour. The last column carries a second
                                 one on its right edge, which has no neighbour to work with. -->
                            <div
                                class="aw-resize"
                                data-testid="pane-resize"
                                *ngIf="i > 0"
                                (mousedown)="onResizeStart($event, i - 1)"
                            ></div>
                            <div
                                class="aw-resize aw-resize--end"
                                data-testid="pane-resize"
                                *ngIf="i === panes.length - 1 && showEndDivider"
                                (mousedown)="onResizeStart($event, i)"
                            ></div>

                            <div class="aw-pane__head">
                                <span class="ham-icon aw-pane__grip" data-testid="pane-handle" cdkDragHandle>
                                    <i class="dots-grid-icon"></i>
                                </span>

                                <!-- allowFreeText=false nails the field to the five kinds; the default would
                                     turn it into a search box with free input. -->
                                <input
                                    hamSimpleDropDown
                                    class="aw-pane__kind"
                                    data-testid="pane-kind"
                                    [options]="paneKindOptions"
                                    [optionKeys]="['label']"
                                    returnKey="id"
                                    [allowFreeText]="false"
                                    [ngModel]="pane.kind"
                                    [ngModelOptions]="{ standalone: true }"
                                    (ngModelChange)="onPaneKindChange(pane.id, $event)"
                                />

                                <ham-button
                                    class="aw-pane__close"
                                    data-testid="pane-close"
                                    variant="transparent"
                                    size="xs"
                                    [hideText]="true"
                                    icon="/assets/hamIcons/x.svg"
                                    [disabled]="panes.length <= 1"
                                    [attr.aria-disabled]="panes.length <= 1"
                                    (buttonClick)="paneClose.emit(pane.id)"
                                ></ham-button>
                            </div>

                            <div class="aw-pane__body" data-testid="pane-body">
                                <ng-container
                                    *ngTemplateOutlet="paneContent; context: { $implicit: pane }"
                                ></ng-container>
                            </div>
                        </div>
                    </ng-container>
                </div>

                <!-- The whole column is the button: it lights up as one strip, the + and its label in it. -->
                <ham-button class="aw-pane-add" data-testid="pane-add" variant="plain" (buttonClick)="paneAdd.emit()">
                    <span class="aw-pane-add__circle" aria-hidden="true">
                        <lucide-icon name="plus" [size]="16"></lucide-icon>
                    </span>
                    <span class="aw-pane-add__label">{{ 'abcp_workspace_pane_add' | i18next }}</span>
                </ham-button>
            </div>
        </ng-template>
    </ng-template>
</div>

<!-- Header and footer are ours: the title input renders plain text, so the subtitle right under the
     title has nowhere to go. -->
<ham-modal
    *ngIf="reviewDialog"
    [hideHeader]="true"
    [hideFooter]="true"
    size="sm"
    (cancel)="reviewMarkCancel.emit()"
>
    <div class="aw-dialog" data-testid="review-dialog">
        <div class="aw-dialog__head">
            <span class="ham-icon aw-dialog__flag" aria-hidden="true"><i class="flag-icon"></i></span>
            <div class="aw-dialog__titles">
                <div class="aw-dialog__title">{{ 'abcp_workspace_review_dialog_title' | i18next }}</div>
                <div class="aw-dialog__account">
                    {{
                        'abcp_workspace_review_dialog_account'
                            | i18next: { number: account?.number, name: account?.name }
                    }}
                </div>
            </div>
            <ham-button
                class="aw-dialog__close"
                data-testid="review-dialog-close"
                variant="transparent"
                size="xs"
                icon="assets/hamIcons/x.svg"
                [hideText]="true"
                (buttonClick)="reviewMarkCancel.emit()"
            ></ham-button>
        </div>

        <label class="aw-dialog__question">{{ 'abcp_workspace_review_dialog_question' | i18next }}</label>
        <textarea
            hamInput
            class="aw-dialog__comment"
            data-testid="review-dialog-comment"
            [hideRemoveXButton]="true"
            [placeholder]="'abcp_workspace_review_dialog_placeholder' | i18next"
            [ngModel]="commentDraft"
            [ngModelOptions]="{ standalone: true }"
            (ngModelChange)="commentDraft = $event"
        ></textarea>

        <p class="aw-dialog__hint">{{ 'abcp_workspace_review_dialog_hint' | i18next }}</p>

        <!-- REV-22005 — the write failed; the dialog stays open with the comment as typed. -->
        <p class="aw-dialog__error" data-testid="review-dialog-error" role="alert" *ngIf="reviewError">
            {{ reviewError }}
        </p>

        <div class="aw-dialog__actions">
            <ham-button
                data-testid="review-dialog-cancel"
                variant="white"
                outline
                (buttonClick)="reviewMarkCancel.emit()"
            >
                {{ 'abcp_workspace_cancel' | i18next }}
            </ham-button>

            <ham-button
                data-testid="review-dialog-confirm"
                variant="teal"
                icon="/assets/hamIcons/flag.svg"
                [disabled]="!commentDraft || saving"
                [attr.aria-disabled]="!commentDraft || saving"
                (buttonClick)="reviewMarkConfirm.emit(commentDraft)"
            >
                {{ 'abcp_workspace_review_dialog_confirm' | i18next }}
            </ham-button>
        </div>
    </div>
</ham-modal>

<!-- An absent amount becomes a dash; the currency code travels with its own amount. -->
<ng-template #money let-value>
    <ng-container *ngIf="value; else missing"
        ><span class="aw-balance__currency" *ngIf="value.currency">{{ value.currency }} </span
        >{{ value.amount | finNumber: '1.2-2' }}</ng-container
    >
    <ng-template #missing>—</ng-template>
</ng-template>
